/**
 * MarketRecapAdminCard — the ad-hoc composer used by admins to publish
 * Market Recap posts without needing curl. Lists every existing post
 * (published + drafts), lets the admin edit any one, and supports a
 * simple "publish now" workflow. Rich markdown editing is a plain
 * textarea with a client-side preview panel — no third-party editor
 * dependency needed.
 */
import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Newspaper, Plus, Save, Trash2, Eye, X } from "lucide-react";

// Very small markdown → HTML renderer (headings, bold, italics, links, bullets).
// Kept inline so the composer preview stays snappy without a real MD library.
function renderMd(md = "") {
  const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  let html = esc(md)
    .replace(/^###\s+(.*)$/gm, "<h3>$1</h3>")
    .replace(/^##\s+(.*)$/gm, "<h2>$1</h2>")
    .replace(/^#\s+(.*)$/gm, "<h1>$1</h1>")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noreferrer">$1</a>')
    .replace(/^-\s+(.*)$/gm, "<li>$1</li>")
    .replace(/(<li>[\s\S]+?<\/li>)/g, "<ul>$1</ul>")
    .replace(/\n{2,}/g, "</p><p>");
  return `<p>${html}</p>`;
}

const EMPTY_FORM = { id: null, title: "", summary: "", body_md: "", tags: "", is_published: true };

export default function MarketRecapAdminCard() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/market-recap");
      setPosts(r.data.items || []);
    } catch (err) {
      toast.error("Could not load posts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startNew = () => setForm(EMPTY_FORM);

  const startEdit = (p) => setForm({
    id: p.id,
    title: p.title,
    summary: p.summary || "",
    body_md: p.body_md || "",
    tags: (p.tags || []).join(", "),
    is_published: !!p.is_published,
  });

  const save = async () => {
    if (!form.title.trim() || !form.body_md.trim()) {
      toast.error("Title and body are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        summary: form.summary.trim() || form.title.slice(0, 200),
        body_md: form.body_md,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
        is_published: form.is_published,
      };
      if (form.id) {
        await api.patch(`/admin/market-recap/${form.id}`, payload);
        toast.success("Post updated");
      } else {
        await api.post("/admin/market-recap", payload);
        toast.success(form.is_published ? "Post published" : "Draft saved");
      }
      setForm(EMPTY_FORM);
      await load();
    } catch (err) {
      toast.error("Save failed — check title and body");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this post permanently?")) return;
    try {
      await api.delete(`/admin/market-recap/${id}`);
      toast.success("Deleted");
      if (form.id === id) setForm(EMPTY_FORM);
      await load();
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="market-recap-admin-card">
      <Card>
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Newspaper className="w-5 h-5 text-primary" />
              <h2 className="serif text-xl">Market Recap posts</h2>
              <Badge variant="secondary" className="ml-2">{posts.length}</Badge>
            </div>
            <Button size="sm" onClick={startNew} data-testid="mr-new-post-btn">
              <Plus className="w-4 h-4 mr-1" /> New post
            </Button>
          </div>

          {loading ? (
            <div className="text-sm text-muted-foreground">Loading…</div>
          ) : posts.length === 0 ? (
            <div className="text-sm text-muted-foreground">No posts yet — create one below.</div>
          ) : (
            <div className="divide-y divide-border">
              {posts.map((p) => (
                <div key={p.id} className="py-2.5 flex items-center gap-3" data-testid={`mr-post-${p.slug}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{p.title}</div>
                    <div className="text-[11px] text-muted-foreground mono uppercase tracking-widest">
                      {p.source} · {p.is_published ? "Published" : "Draft"} · {new Date(p.published_at).toLocaleDateString()}
                    </div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => startEdit(p)} data-testid={`mr-edit-${p.slug}`}>Edit</Button>
                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => remove(p.id)} data-testid={`mr-delete-${p.slug}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="serif text-lg">{form.id ? "Edit post" : "New post"}</h3>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="ghost" onClick={() => setPreview((v) => !v)} data-testid="mr-form-toggle-preview">
                {preview ? <><X className="w-3.5 h-3.5 mr-1" /> Close preview</> : <><Eye className="w-3.5 h-3.5 mr-1" /> Preview</>}
              </Button>
            </div>
          </div>

          <div className="grid gap-3">
            <div>
              <Label htmlFor="mr-title">Title</Label>
              <Input
                id="mr-title"
                data-testid="mr-form-title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Rate cuts kick in — 4 dividend growers to watch"
                maxLength={200}
              />
            </div>
            <div>
              <Label htmlFor="mr-summary">Summary (one line, appears in the list + RSS)</Label>
              <Input
                id="mr-summary"
                data-testid="mr-form-summary"
                value={form.summary}
                onChange={(e) => setForm({ ...form, summary: e.target.value })}
                placeholder="Optional — defaults to the first 200 characters of the title."
                maxLength={400}
              />
            </div>
            <div>
              <Label htmlFor="mr-body">Body (Markdown supported)</Label>
              <Textarea
                id="mr-body"
                data-testid="mr-form-body"
                rows={14}
                className="font-mono text-sm"
                value={form.body_md}
                onChange={(e) => setForm({ ...form, body_md: e.target.value })}
                placeholder="## What happened&#10;&#10;Markets shrugged today, but 3 dividend growers quietly notched 52-week highs…"
              />
            </div>
            {preview && form.body_md && (
              <div className="rounded-md border border-border p-4 bg-muted/30" data-testid="mr-form-preview">
                <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Preview</div>
                <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: renderMd(form.body_md) }} />
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="mr-tags">Tags (comma-separated)</Label>
                <Input
                  id="mr-tags"
                  data-testid="mr-form-tags"
                  value={form.tags}
                  onChange={(e) => setForm({ ...form, tags: e.target.value })}
                  placeholder="weekly, analysis, ratecuts"
                />
              </div>
              <div className="flex items-end justify-between border border-border rounded-md px-3 py-2">
                <div>
                  <div className="text-sm font-medium">Publish immediately</div>
                  <div className="text-xs text-muted-foreground">Off = save as draft (not visible publicly)</div>
                </div>
                <Switch
                  data-testid="mr-form-published-toggle"
                  checked={form.is_published}
                  onCheckedChange={(v) => setForm({ ...form, is_published: v })}
                />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            {form.id && (
              <Button variant="ghost" onClick={() => setForm(EMPTY_FORM)} data-testid="mr-form-cancel">
                Cancel edit
              </Button>
            )}
            <Button onClick={save} disabled={saving} data-testid="mr-form-submit">
              <Save className="w-4 h-4 mr-1" />
              {saving ? "Saving…" : (form.is_published ? "Publish" : "Save draft")}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
